// client/src/pages/sections/groups/resolveActionQueue.js
//
// Single chained-Promise serializer for every POST /api/diResolveAction
// call in this tab. Without serialization, two near-simultaneous calls
// (e.g., partner clicks Accept while a prior MarkNlc is still
// revalidating) can race against the server-side rebuildDqIssues
// DELETE+INSERT cycle: the second call's dispatcher SELECTs against a
// transiently-empty open-issues table and returns 404 even though the
// row will exist a few ms later.
//
// All Client Groups action callers (ResolveModal, SplitMemberButton,
// MoveToRgButton, MarkNlcButton, PromoteSgParentButton,
// MigrateMembersModal) route through postResolveAction() so the
// optimistic UI still feels instant but the network round-trips
// happen one at a time.
//
// The chain is process-global within the browser tab. Multi-tab and
// multi-user races still hit the server-side mutex in revalidate.js
// + the unique index on dq_issues for the durable defense.

import { getDemo } from '../../../lib/tourDemo';

let chain = Promise.resolve();
let last = null;              // { key: JSON body, promise } of the most recently queued request, until it settles

// The Client Groups section is opened under a SPECIFIC period (route param).
// With concurrent active periods the server can no longer infer "the" active
// period for client-level verbs, so every diResolveAction body must carry the
// period. ClientGroupsSection sets it once; postResolveAction injects it.
// (Issue-level verbs ignore body.period and use the issue's own period.)
let actionPeriod = null;
export function setActionPeriod(period) { actionPeriod = period || null; }
// Read the current Client Groups period — for period-scoped GET helpers
// (candidate pickers) that don't go through postResolveAction.
export function getActionPeriod() { return actionPeriod; }

/**
 * POST a body to /api/diResolveAction in serialized order.
 * Returns the parsed JSON body on 2xx. Throws an Error with `.status`
 * and `.body` on non-2xx so callers can branch on HTTP code.
 *
 * @param {object} body  request payload (verb, issueId/clientCode, etc.)
 * @returns {Promise<any>}
 */
export function postResolveAction(body) {
  // Spotlight tour demo mode: the page is showing curated fixtures, so a
  // curious click must never reach the server. Resolve a benign shape so
  // callers' optimistic UI + onResolved refetch (which re-serves demo data)
  // proceed without mutating production.
  if (getDemo()) return Promise.resolve({ demo: true, ok: true });
  const payload = (body && body.period) ? body : { ...body, period: actionPeriod };
  async function run() {
    const res = await fetch('/api/diResolveAction', {
      method:      'POST',
      headers:     { 'Content-Type': 'application/json' },
      credentials: 'include',
      body:        JSON.stringify(payload),
    });
    const text = await res.text();
    if (!res.ok) {
      const err = new Error(text || `HTTP ${res.status}`);
      err.status = res.status;
      err.body   = text;
      throw err;
    }
    return text ? JSON.parse(text) : {};
  }
  // A request identical to the one queued LAST, while that one has not settled, is not sent again: a double-click (or a
  // second click while the first answer is on its way) gets the first request's answer instead of writing twice. Only the
  // most recent request counts, so the deliberate rapid toggles (confirm, unconfirm, confirm — Relationship Group rows)
  // are never swallowed: the third differs from the one queued just before it. Once settled, the same action is new.
  const key = JSON.stringify(payload);
  if (last && last.key === key) return last.promise;
  // Chain onto the prior call regardless of its outcome — a failed
  // call shouldn't block the queue. .catch(()=>{}) on the chain
  // guarantees the Promise never settles to rejected, so future
  // callers always see a resolved predecessor.
  const myTurn = chain.then(run, run);
  chain = myTurn.catch(() => {});
  const tracked = myTurn.finally(() => { if (last && last.promise === tracked) last = null; });
  last = { key, promise: tracked };
  return tracked;
}
